<template>
  <div :class="$vuetify.breakpoint.xsOnly ? 'px-4' : ''">
    <v-card :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''" class="mb-4">
      <v-card-title class="subtitle-1 text-uppercase font-weight-medium d-flex align-center">
        <v-icon class="mr-2">mdi-domain</v-icon>
        {{ $t('quotations.recipients.card_title') }}
      </v-card-title>
      <v-card-text>
        <quotation-request-editor
          v-if="editorOpen"
          :request="request"
          :parties="parties"
          :persons="persons"
          :saving="saving"
          :closable="persisted"
          @done="editing = false"
          @cancel="cancelEdit"
        />
        <template v-else>
          <v-row v-if="$vuetify.breakpoint.mdAndUp" dense class="caption grey--text requests-header px-2">
            <v-col md="4">{{ $t('quotations.recipients.party') }}</v-col>
            <v-col md="2">{{ $t('quotations.recipients.code') }}</v-col>
            <v-col md="2">{{ $t('quotations.recipients.deadline') }}</v-col>
            <v-col md="2">{{ $t('quotations.recipients.sent') }}</v-col>
            <v-col md="2">{{ $t('quotations.recipients.result') }}</v-col>
          </v-row>
          <quotation-request-view
            :request="request"
            :party-name="partyName"
            :person-name="personName"
            :version="quotation.version"
            :persisted="persisted"
            :can-send="quotation.status !== 'REQUEST'"
            :busy="saving || busy"
            :result-options="resultOptions"
            @edit="startEdit"
            @dates="v => update({ dates: v })"
            @result="changeResult"
            @document="$refs.documentDialog.open()"
          />
        </template>
      </v-card-text>
    </v-card>

    <quotation-document-dialog
      ref="documentDialog"
      :quotation-id="quotation.id"
      :request="request"
      :party-name="partyName"
      :busy="saving || busy"
      @sent="markSent"
    />
    <status-reason-dialog ref="reasonDialog" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import quotationApi, { blockers } from '@/api/quotation';
import QuotationDocumentDialog from './QuotationDocumentDialog';
import StatusReasonDialog from './StatusReasonDialog';
import QuotationRequestView from './QuotationRequestView';
import QuotationRequestEditor from './QuotationRequestEditor';

// Muokkaimessa muokattavat kentät
const EDITABLE = ['partyId', 'personId', 'receivedDate', 'deadline', 'deadlineTime', 'channel', 'source', 'note', 'dates'];

// Tarjouksen tilaaja ja tarjouspyyntö. Uusi tilaaja saadaan kopioimalla tarjous. Lähetys ja tulos tallentuvat heti
// omalla kutsulla; muut kentät tallentuvat tarjouksen mukana.
export default {
  name: 'QuotationRequestCard',
  components: { StatusReasonDialog, QuotationDocumentDialog, QuotationRequestView, QuotationRequestEditor },
  props: {
    quotation: { type: Object, required: true },
    parties: { type: Array, default: () => [] },
    persons: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    persisted: { type: Boolean, default: false },
  },
  data() {
    return {
      busy: false,
      editing: false,
      // Kentät ennen muokkausta (peruutus)
      editingBackup: null,
      resultOptions: [
        { text: this.$t('quotations.recipients.results.none'), value: null },
        { text: this.$t('quotations.recipients.results.ORDERED'), value: 'ORDERED' },
        { text: this.$t('quotations.recipients.results.REJECTED'), value: 'REJECTED' },
        { text: this.$t('quotations.recipients.results.NO_ANSWER'), value: 'NO_ANSWER' },
      ],
    };
  },
  computed: {
    request() {
      return this.quotation.recipient;
    },
    // Uudella tarjouksella ja tilaajattomalla (ennen yhden tilaajan mallia luodulla) tarjouksella muokkain on auki
    editorOpen() {
      return !this.persisted || this.editing || !this.request.partyId;
    },
    partyName() {
      const party = this.parties.find(p => p.id === this.request.partyId);
      return party ? party.companyName : '';
    },
    personName() {
      const person = this.persons.find(p => p.id === this.request.personId);
      return person ? `${person.firstname} ${person.lastname}` : '';
    },
  },
  methods: {
    startEdit() {
      this.editingBackup = EDITABLE.reduce((b, k) => ({ ...b, [k]: this.request[k] }), {});
      this.editing = true;
    },
    cancelEdit() {
      if (this.editingBackup) Object.keys(this.editingBackup).forEach(k => this.$set(this.request, k, this.editingBackup[k]));
      this.editing = false;
    },
    async update(data, options) {
      this.busy = true;
      try {
        const updated = await quotationApi.updateRecipient(this.quotation.id, data, options);
        this.$emit('recipient-updated', updated);
        this.$showSuccessNotification(this.$t('quotations.notifications.recipient_updated'));
      } catch (error) {
        this.$handleApiError(error, error && error.message);
      }
      this.busy = false;
    },
    // Lähetys ennen Valmis-vaihetta: kysytään, merkitäänkö sisältö samalla valmiiksi
    async markSent() {
      const markReady = this.quotation.status !== 'READY';
      if (markReady) {
        const missing = blockers(this.quotation);
        if (missing.length > 0) {
          this.$showWarningNotification(missing.map(b => this.$t(`quotations.blockers.${b}`)).join(', '));
          return;
        }
        const ok = await this.$confirm('primary', this.$t('quotations.recipients.sent_today'), this.$t('quotations.recipients.mark_ready_and_sent'));
        if (!ok) return;
      }
      this.update({ sentDate: dayjs().format('YYYY-MM-DD') }, { markReady });
    },
    async changeResult(value) {
      if (value === 'ORDERED') {
        this.$emit('accept');
        return;
      }
      const today = dayjs().format('YYYY-MM-DD');
      let resultReason = null;
      if (value === 'REJECTED') {
        const reason = await this.$refs.reasonDialog.open('reject', this.$t('quotations.recipients.reject_reason_title'));
        if (!reason) return;
        resultReason = [reason.code, reason.text].filter(Boolean).join(' – ');
      }
      this.update({ result: value, resultDate: value ? today : null, resultReason });
    },
  },
};
</script>

<style scoped>
.requests-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
